const OUTLOOK_ADDIN_AUTH_STORAGE_KEY = 'auth_state_outlook_addin'
const EXPIRY_SKEW_MS = 30 * 1000
const POPUP_POLL_INTERVAL_MS = 120
const POPUP_TIMEOUT_MS = 120000

interface OutlookAddinAuthState {
  token?: string
  // Unix epoch milliseconds
  expires_at?: number
}

function readState(): OutlookAddinAuthState {
  try {
    const raw = localStorage.getItem(OUTLOOK_ADDIN_AUTH_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as OutlookAddinAuthState
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeState(state: OutlookAddinAuthState): void {
  try {
    localStorage.setItem(OUTLOOK_ADDIN_AUTH_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Best-effort cache only.
  }
}

export function getOutlookAddinToken(): string | null {
  const state = readState()
  const token = state.token?.trim()
  if (!token) return null

  if (typeof state.expires_at === 'number') {
    const now = Date.now()
    if (state.expires_at <= now + EXPIRY_SKEW_MS) {
      clearOutlookAddinToken()
      return null
    }
  }

  return token
}

export function setOutlookAddinToken(token: string, expiresOn?: Date | null): void {
  const trimmed = token.trim()
  if (!trimmed) return

  writeState({
    token: trimmed,
    expires_at: expiresOn?.getTime(),
  })
}

export function clearOutlookAddinToken(): void {
  try {
    localStorage.removeItem(OUTLOOK_ADDIN_AUTH_STORAGE_KEY)
  } catch {
    // Ignore storage failures.
  }
}

export async function signInWithBackendPopup(): Promise<string> {
  const baseApiUrl = (import.meta.env.VITE_API_URL || window.location.origin).replace(/\/+$/, '')
  const loginUrl = `${baseApiUrl}/api/auth/login`
  const popup = window.open(
    loginUrl,
    'giatec_outlook_addin_login',
    'width=540,height=760,resizable=yes,scrollbars=yes'
  )

  if (!popup) {
    throw new Error('Sign-in popup was blocked. Please allow popups and try again.')
  }

  return await new Promise<string>((resolve, reject) => {
    const startedAt = Date.now()

    const cleanup = () => {
      window.clearInterval(timer)
      window.clearTimeout(timeout)
    }

    const finishWithError = (message: string) => {
      cleanup()
      try {
        popup.close()
      } catch {
        // Ignore close failures.
      }
      reject(new Error(message))
    }

    const finishWithToken = (token: string) => {
      cleanup()
      try {
        popup.close()
      } catch {
        // Ignore close failures.
      }
      resolve(token)
    }

    const timer = window.setInterval(() => {
      if (popup.closed) {
        finishWithError('Sign-in cancelled before completion.')
        return
      }

      let href: string
      try {
        href = popup.location.href
      } catch {
        // Still on a cross-origin page (e.g. Microsoft login); keep polling.
        return
      }

      if (!href) return

      let url: URL
      try {
        url = new URL(href)
      } catch {
        return
      }

      const token = url.searchParams.get('token')?.trim()
      if (token) {
        finishWithToken(token)
        return
      }

      const authError = url.searchParams.get('error')?.trim()
      if (authError) {
        finishWithError(`Authentication failed: ${authError}`)
        return
      }

      // Stop if popup returned to our app without a token for too long.
      if (url.origin === window.location.origin && Date.now() - startedAt > 5000) {
        finishWithError('Authentication completed but no token was returned.')
      }
    }, POPUP_POLL_INTERVAL_MS)

    const timeout = window.setTimeout(() => {
      finishWithError('Sign-in timed out. Please try again.')
    }, POPUP_TIMEOUT_MS)
  })
}
